import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import {
  inspectionService,
  WARRANTY_STATUS_LABELS,
  COMMERCIAL_STATUS_LABELS,
  CUSTOMER_APPROVAL_LABELS,
  type Finding,
} from '@/services/inspection.service';
import { FindingAccordionItem } from '@/features/inspection/components/FindingAccordionItem';
import { ProcedureExecutionPanel } from '@/features/inspection/components/ProcedureExecutionPanel';
import { InternalNotesSection } from '@/features/inspection/components/InternalNotesSection';
import { OwnershipHistoryPanel } from '@/features/inspection/components/ownership/OwnershipHistoryPanel';
import { SkeletonPage } from '@/components/skeletons';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/hooks/use-permissions';
import { ROUTES } from '@/routes/routes';
import { CollapsibleSection } from './components/CollapsibleSection';
import { FeasibilityReviewPanel } from './components/FeasibilityReviewPanel';
import { JobSummaryCard } from './components/JobSummaryCard';
import { InspectionTimeline } from './components/InspectionTimeline';
import { ApproveInspectionButton } from './components/ApproveInspectionButton';
import {
  ReturnToEngineerDialog,
  type ReturnToEngineerTarget,
} from './components/ReturnToEngineerDialog';

// no-op — findings are never editable/deletable in this workspace (canModify
// is always false here), but FindingAccordionItem's props are required.
function noop() {}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString();
}

export function OpsReviewWorkspacePage() {
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const can = useHasPermission();
  const [returnTarget, setReturnTarget] = useState<ReturnToEngineerTarget | null>(null);

  const {
    data: inspection,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['inspection', inspectionId],
    queryFn: () => inspectionService.get(inspectionId!),
    enabled: !!inspectionId,
  });

  const { data: findingsData, isLoading: findingsLoading } = useQuery({
    queryKey: ['inspection-findings', inspectionId],
    queryFn: () => inspectionService.getFindings(inspectionId!, { pageSize: 100 }),
    enabled: !!inspectionId,
  });

  const goToWorkQueue = () => navigate(ROUTES.OPS_WORK_QUEUE);

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    void qc.invalidateQueries({ queryKey: ['ops-work-queue'] });
    void qc.invalidateQueries({ queryKey: ['dashboard-operations'] });
  };

  if (isLoading) return <SkeletonPage />;

  if (isError || !inspection) {
    return (
      <div className="space-y-5">
        <Breadcrumb
          segments={[
            { label: 'Operations', to: ROUTES.DASHBOARD_OPERATIONS },
            { label: 'Work Queue', to: ROUTES.OPS_WORK_QUEUE },
            { label: 'Review Workspace' },
          ]}
        />
        <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="h-10 w-10 text-muted-foreground"
          />
          <p className="text-lg font-semibold text-foreground">Inspection not found</p>
          <Button variant="ghost" onClick={goToWorkQueue}>
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2 h-4 w-4" />
            Back to Work Queue
          </Button>
        </div>
      </div>
    );
  }

  const hasCommercialAccess = can('inspection:view-commercial');
  // D-131 M5 — the round reads are gated server-side on `inspection:view-commercial`
  // (the D-021 read permission Operations holds); the verdict write on
  // `inspection:modification:review`. Either permission is reason to show the
  // panel; a 403 from the API is handled inside it.
  const showModificationPanel = hasCommercialAccess || can('inspection:modification:review');
  const canOpsReview = inspection.status === 'COMPLETED' && can('inspection:operations-review');
  const canCreateNote = can('inspection:notes:create');
  const canRespondAuthorization = can('inspection:authorization:respond');
  const canViewOwnershipHistory = can('inspection:ownership:history');
  const canViewProcedure = can('procedure:view-execution');

  const findings = findingsData?.data ?? [];

  return (
    <div className="space-y-5 pb-4">
      <Breadcrumb
        segments={[
          { label: 'Operations', to: ROUTES.DASHBOARD_OPERATIONS },
          { label: 'Work Queue', to: ROUTES.OPS_WORK_QUEUE },
          { label: 'Review Workspace' },
        ]}
      />

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={goToWorkQueue}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Back to Work Queue"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-lg font-semibold text-foreground">Operations Review</h1>
          <p className="text-xs text-muted-foreground">Review the job, then Approve or Return.</p>
        </div>
      </div>

      <JobSummaryCard inspection={inspection} />

      {/* ── Modification Request — Operations feasibility (D-131 M5) ─────── */}
      {showModificationPanel && (
        <CollapsibleSection title="Modification Request">
          <FeasibilityReviewPanel inspectionId={inspectionId!} />
        </CollapsibleSection>
      )}

      {/* ── Customer & Equipment ──────────────────────────────────────────── */}
      <CollapsibleSection title="Customer & Equipment">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground">Customer</p>
            <p className="text-sm text-foreground">{inspection.customerName ?? '—'}</p>
          </div>
          {inspection.customerLocationName && (
            <div>
              <p className="text-xs text-muted-foreground">Location</p>
              <p className="text-sm text-foreground">{inspection.customerLocationName}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground">Equipment</p>
            <p className="text-sm text-foreground">{inspection.equipmentName}</p>
          </div>
          {inspection.equipmentSerialNumber && (
            <div>
              <p className="text-xs text-muted-foreground">Serial Number</p>
              <p className="font-mono text-sm text-foreground">
                {inspection.equipmentSerialNumber}
              </p>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground">Warranty</p>
            <p className="text-sm text-foreground">
              {WARRANTY_STATUS_LABELS[inspection.warrantyStatus]}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Assigned Engineer</p>
            <p className="text-sm text-foreground">{inspection.assignedEngineerName ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Assigned By</p>
            <p className="text-sm text-foreground">{inspection.assignedByName ?? '—'}</p>
          </div>
        </div>
      </CollapsibleSection>

      {/* ── Inspection Summary ────────────────────────────────────────────── */}
      <CollapsibleSection title="Inspection Summary">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground">Scheduled</p>
            <p className="text-sm text-foreground">{fmtDateTime(inspection.scheduledAt)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Started</p>
            <p className="text-sm text-foreground">{fmtDateTime(inspection.startedAt)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Completed</p>
            <p className="text-sm text-foreground">{fmtDateTime(inspection.completedAt)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Total Findings</p>
            <p className="text-sm text-foreground">{inspection.totalFindingsCount}</p>
          </div>
          {inspection.intakeNumber && (
            <div>
              <p className="text-xs text-muted-foreground">Inward #</p>
              <p className="font-mono text-sm text-foreground">{inspection.intakeNumber}</p>
            </div>
          )}
        </div>

        {inspection.customerRequestedScope && (
          <div className="mt-4">
            <p className="text-xs text-muted-foreground">Customer Requested Scope</p>
            <p className="mt-1 text-sm text-foreground">{inspection.customerRequestedScope}</p>
          </div>
        )}

        {inspection.notes && (
          <div className="mt-4">
            <p className="text-xs text-muted-foreground">Notes</p>
            <p className="mt-1 text-sm text-foreground">{inspection.notes}</p>
          </div>
        )}

        {inspection.operationsReturnReason && (
          <div className="mt-4 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
            <span className="text-xs font-medium text-destructive">Previous Return Reason</span>
            <p className="mt-1 text-sm text-foreground">{inspection.operationsReturnReason}</p>
          </div>
        )}
      </CollapsibleSection>

      {/* ── Procedure Execution (self-collapsing) ─────────────────────────── */}
      {canViewProcedure && (
        <ProcedureExecutionPanel inspectionId={inspectionId!} canExecute={false} />
      )}

      {/* ── Findings (includes per-finding Authorization, read-only) ─────── */}
      <CollapsibleSection title={`Findings (${findings.length})`}>
        {findingsLoading ? (
          <p className="text-sm text-muted-foreground">Loading findings…</p>
        ) : findings.length === 0 ? (
          <p className="text-sm text-muted-foreground">No findings recorded for this inspection.</p>
        ) : (
          <div className="space-y-2">
            {findings.map((finding: Finding) => (
              <FindingAccordionItem
                key={finding.id}
                finding={finding}
                inspectionId={inspectionId!}
                canModify={false}
                canUploadAttachments={false}
                canDeleteMedia={false}
                canCreateFindingNote={false}
                canCreateAuthorization={false}
                canRespondAuthorization={canRespondAuthorization}
                onEdit={noop}
                onDelete={noop}
              />
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* ── Commercial & Customer Context (read-only, D-021) ──────────────── */}
      {hasCommercialAccess && (
        <CollapsibleSection title="Commercial & Customer Context">
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Commercial Review
              </p>
              {inspection.commercialReviewStatus ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className="text-sm text-foreground">
                      {COMMERCIAL_STATUS_LABELS[inspection.commercialReviewStatus]}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Reviewed By</p>
                    <p className="text-sm text-foreground">
                      {inspection.commercialReviewedByName ?? '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Sent At</p>
                    <p className="text-sm text-foreground">
                      {fmtDateTime(inspection.commercialSentAt)}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Not yet available.</p>
              )}
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Customer Approval
              </p>
              {inspection.customerApprovalStatus ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className="text-sm text-foreground">
                      {CUSTOMER_APPROVAL_LABELS[inspection.customerApprovalStatus]}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Responded At</p>
                    <p className="text-sm text-foreground">
                      {fmtDateTime(inspection.customerApprovalRespondedAt)}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Awaiting commercial review.</p>
              )}
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* ── OPS Notes ─────────────────────────────────────────────────────── */}
      <CollapsibleSection title="OPS Notes">
        <InternalNotesSection inspectionId={inspectionId!} canCreate={canCreateNote} />
      </CollapsibleSection>

      {/* ── Timeline (before Ownership History, per instruction) ─────────── */}
      <CollapsibleSection title="Timeline" defaultOpen={false}>
        <InspectionTimeline inspectionId={inspectionId!} />
      </CollapsibleSection>

      {/* ── Ownership History ─────────────────────────────────────────────── */}
      {canViewOwnershipHistory && (
        <CollapsibleSection title="Ownership History" defaultOpen={false}>
          <OwnershipHistoryPanel inspectionId={inspectionId!} />
        </CollapsibleSection>
      )}

      {/* ── Sticky action footer ───────────────────────────────────────────── */}
      <div className="sticky bottom-0 z-10 -mx-6 border-t border-border bg-background/95 px-6 py-3 backdrop-blur-sm">
        <div className="flex items-center justify-end gap-3">
          {canOpsReview ? (
            <>
              <Button
                variant="secondary"
                onClick={() =>
                  setReturnTarget({
                    id: inspection.id,
                    inspectionNumber: inspection.inspectionNumber,
                  })
                }
              >
                Return
              </Button>
              <ApproveInspectionButton
                inspectionId={inspection.id}
                inspectionNumber={inspection.inspectionNumber}
                onApproved={invalidateAll}
              />
            </>
          ) : (
            <span className="text-sm text-muted-foreground">
              No review action available in the current state.
            </span>
          )}
        </div>
      </div>

      <ReturnToEngineerDialog
        target={returnTarget}
        onClose={() => setReturnTarget(null)}
        onReturned={() => {
          setReturnTarget(null);
          invalidateAll();
        }}
      />
    </div>
  );
}
